import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Avatar, Descriptions, Divider, Drawer, List, message, Popconfirm, Space, Spin, Tag, Typography} from 'antd';
import {ApiOutlined, DeleteOutlined, EditOutlined, LinkOutlined, MenuOutlined, SettingOutlined, UserOutlined} from '@ant-design/icons';
import PermissionButton from './common/PermissionButton';
import GroupUserMembersModal from './GroupUserMembersModal';
import GroupMenuMembersModal from './GroupMenuMembersModal';
import GroupApiMembersModal from './GroupApiMembersModal';
import GroupEditModal from './GroupEditModal';
import userApi from '../api/userApi';
import {getMenus} from '../api/menuApi';
import {getApis} from '../api/apiApi';
import {
  deleteGroup,
  updateGroup,
  updateUserGroupMembers,
  updateMenuGroupMembers,
  updateApiGroupMembers
} from '../api/groupApi';
import {pickList} from '../api/http';

const {Text} = Typography;

const TYPE_LABEL = {
  USER_GROUP: '사용자그룹',
  MENU_GROUP: '메뉴그룹',
  API_GROUP: 'API그룹'
};

const TYPE_COLOR = {
  USER_GROUP: 'cyan',
  MENU_GROUP: 'blue',
  API_GROUP: 'geekblue'
};

const methodColor = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  PATCH: 'gold',
  DELETE: 'red'
};

const GroupDetailDrawer = ({
  open,
  group,
  onClose,
  onDeleted,
  onUpdated,
  canDelete,
  canEdit,
  canEditUserMembers,
  canEditMenuMembers,
  canEditApiMembers,
  permLoading
}) => {
  const [lookupLoading, setLookupLoading] = useState(false);
  const [userLookup, setUserLookup] = useState({});
  const [menuLookup, setMenuLookup] = useState({});
  const [apiLookup, setApiLookup] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const lookupsLoadedRef = useRef(false);

  // 모든 lookup 데이터를 한번에 로드 (AuthorityDetailDrawer와 동일한 방식)
  useEffect(() => {
    if (!open) return;
    if (lookupsLoadedRef.current) return;

    let ignore = false;

    const loadLookups = async () => {
      setLookupLoading(true);
      try {
        const [usersRes, menusRes, apisRes] = await Promise.all([
          userApi.listUsers().catch((err) => {
            console.error('사용자 목록 조회 실패:', err);
            return null;
          }),
          getMenus().catch((err) => {
            console.error('메뉴 목록 조회 실패:', err);
            return null;
          }),
          getApis().catch((err) => {
            console.error('API 목록 조회 실패:', err);
            return null;
          })
        ]);

        if (ignore) return;

        const users = pickList(usersRes);
        const menus = pickList(menusRes);
        const apis = pickList(apisRes);

        const userMap = {};
        (users || []).forEach((user) => {
          if (user?.userId) {
            userMap[String(user.userId)] = user;
          }
        });

        const menuMap = {};
        (menus || []).forEach((menu) => {
          if (menu?.menuId) {
            menuMap[String(menu.menuId)] = menu;
          }
        });

        const apiMap = {};
        (apis || []).forEach((api) => {
          if (api?.apiId) {
            apiMap[String(api.apiId)] = api;
          }
        });

        setUserLookup(userMap);
        setMenuLookup(menuMap);
        setApiLookup(apiMap);
        lookupsLoadedRef.current = true;
      } catch (err) {
        console.error('그룹 대상 정보 조회 실패:', err);
        if (!ignore) {
          setUserLookup({});
          setMenuLookup({});
          setApiLookup({});
        }
      } finally {
        if (!ignore) {
          setLookupLoading(false);
        }
      }
    };

    loadLookups();

    return () => {
      ignore = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setUserModalOpen(false);
      setMenuModalOpen(false);
      setApiModalOpen(false);
    }
  }, [open]);

  const members = useMemo(() => {
    if (!group) return [];
    const ids = Array.isArray(group.trgtIdList) ? group.trgtIdList : [];
    if (!ids.length) return [];

    if (group.groupType === 'USER_GROUP') {
      return ids.map((id) => {
        const key = String(id);
        const user = userLookup[key];
        const primary = user?.userNm || user?.lgnUserNm || key;
        const secondaryParts = [];
        if (user?.lgnUserNm && user.lgnUserNm !== primary) {
          secondaryParts.push(user.lgnUserNm);
        }
        if (user?.eml) {
          secondaryParts.push(user.eml);
        }
        return {
          key,
          type: 'USER_GROUP',
          primary,
          secondary: secondaryParts.join(' • '),
          tag: user?.userId || key,
          tagColor: 'processing'
        };
      });
    }

    if (group.groupType === 'MENU_GROUP') {
      return ids.map((id) => {
        const key = String(id);
        const menu = menuLookup[key];
        const primary = menu?.menuNm || menu?.path || key;
        const secondary = menu?.path && menu.path !== primary ? menu.path : '';
        return {
          key,
          type: 'MENU_GROUP',
          primary,
          secondary,
          tag: menu?.menuId && menu.menuId !== key ? menu.menuId : undefined,
          tagColor: 'purple'
        };
      });
    }

    if (group.groupType === 'API_GROUP') {
      return ids.map((id) => {
        const key = String(id);
        const api = apiLookup[key];
        const method = api?.methodType;
        const url = api?.apiUrl;
        const methodUrl = [method, url].filter(Boolean).join(' ');
        const primary = api?.apiNm || methodUrl || key;
        const secondary = methodUrl && methodUrl !== primary ? methodUrl : '';
        return {
          key,
          type: 'API_GROUP',
          primary,
          secondary,
          tag: method || undefined,
          tagColor: methodColor[method] || 'geekblue'
        };
      });
    }

    return ids.map((id) => ({
      key: String(id),
      type: 'OTHER',
      primary: String(id),
      secondary: '',
      tag: undefined
    }));
  }, [group, userLookup, menuLookup, apiLookup]);

  const handleDelete = async () => {
    if (!group) return;
    if (permLoading || !canDelete) {
      message.warning('삭제 권한이 없습니다.');
      return;
    }
    try {
      setDeleting(true);
      await deleteGroup(group.groupId);
      message.success('그룹이 삭제되었습니다.');
      setDeleting(false);
      onDeleted?.(group);
    } catch (err) {
      console.error('그룹 삭제 실패:', err);
      message.error(err?.message || '그룹 삭제에 실패했습니다.');
      setDeleting(false);
    }
  };

  const handleUserMembersSubmit = async (userIds = []) => {
    if (!group) return;
    if (permLoading || !canEditUserMembers) {
      message.warning('멤버 편집 권한이 없습니다.');
      return;
    }
    try {
      await updateUserGroupMembers(group.groupId, userIds);
      message.success('멤버가 저장되었습니다.');
      setUserModalOpen(false);
      onUpdated?.();
    } catch (err) {
      console.error('사용자 멤버 저장 실패:', err);
      message.error(err?.message || '멤버 저장에 실패했습니다.');
    }
  };

  const handleEditSuccess = () => {
    message.success('그룹 정보가 수정되었습니다.');
    setEditModalOpen(false);
    onUpdated?.();
  };

  const handleMenuMembersSuccess = () => {
    message.success('멤버가 저장되었습니다.');
    setMenuModalOpen(false);
    onUpdated?.();
  };

  const handleApiMembersSuccess = () => {
    message.success('멤버가 저장되었습니다.');
    setApiModalOpen(false);
    onUpdated?.();
  };

  const renderMembers = () => {
    if (!group) return null;

    if (lookupLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
          <Spin size="small" />
          <Text type="secondary">멤버 정보를 불러오는 중...</Text>
        </div>
      );
    }

    if (!members.length) {
      return (
        <Text type="secondary">등록된 항목이 없습니다.</Text>
      );
    }

    if (group.groupType === 'USER_GROUP') {
      return (
        <List
          size="small"
          dataSource={members}
          style={{ maxHeight: 320, overflowY: 'auto' }}
          renderItem={(member) => (
            <List.Item
              key={member.key}
              style={{ padding: '8px 12px', borderRadius: 4, transition: 'background 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Space size={8} align="start" style={{ width: '100%' }}>
                <Avatar size={32} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff', flexShrink: 0 }} />
                <Space direction="vertical" size={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 13 }}>{member.primary}</Text>
                  {member.secondary && (
                    <Text type="secondary" style={{ fontSize: 11 }}>{member.secondary}</Text>
                  )}
                  {member.tag && (
                    <Tag color={member.tagColor || 'processing'} style={{ marginTop: 4, fontSize: 11 }}>ID: {member.tag}</Tag>
                  )}
                </Space>
              </Space>
            </List.Item>
          )}
        />
      );
    }

    if (group.groupType === 'MENU_GROUP') {
      return (
        <List
          size="small"
          dataSource={members}
          style={{ maxHeight: 320, overflowY: 'auto' }}
          renderItem={(member) => (
            <List.Item
              key={member.key}
              style={{ padding: '6px 12px', borderRadius: 4, transition: 'background 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Space size={8} align="start" style={{ width: '100%' }}>
                <MenuOutlined style={{ color: member.tagColor || '#722ed1', fontSize: 16, marginTop: 2 }} />
                <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                  <Space size={6} wrap>
                    <Text strong style={{ fontSize: 13 }}>{member.primary}</Text>
                    {member.tag && <Tag color={member.tagColor || 'purple'} style={{ fontSize: 11 }}>{member.tag}</Tag>}
                  </Space>
                  {member.secondary && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      <LinkOutlined style={{ marginRight: 4 }} />
                      {member.secondary}
                    </Text>
                  )}
                </Space>
              </Space>
            </List.Item>
          )}
        />
      );
    }

    if (group.groupType === 'API_GROUP') {
      return (
        <List
          size="small"
          dataSource={members}
          style={{ maxHeight: 320, overflowY: 'auto' }}
          renderItem={(member) => (
            <List.Item
              key={member.key}
              style={{ padding: '6px 12px', borderRadius: 4, transition: 'background 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Space size={8} align="start" style={{ width: '100%' }}>
                <ApiOutlined style={{ color: member.tagColor || '#1890ff', fontSize: 16, marginTop: 2 }} />
                <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                  <Space size={6} wrap align="center">
                    {member.tag && (
                      <Tag color={member.tagColor || 'geekblue'} style={{ fontSize: 11, fontWeight: 600, margin: 0 }}>
                        {member.tag}
                      </Tag>
                    )}
                    <Text strong style={{ fontSize: 13 }}>{member.primary}</Text>
                  </Space>
                  {member.secondary && (
                    <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {member.secondary}
                    </Text>
                  )}
                </Space>
              </Space>
            </List.Item>
          )}
        />
      );
    }

    return (
      <List
        size="small"
        dataSource={members}
        style={{ maxHeight: 320, overflowY: 'auto' }}
        renderItem={(member) => (
          <List.Item key={member.key}>
            <Space direction="vertical" size={0}>
              <Text>{member.primary}</Text>
              {member.secondary && <Text type="secondary" style={{ fontSize: 11 }}>{member.secondary}</Text>}
            </Space>
          </List.Item>
        )}
      />
    );
  };

  const renderHeaderActions = () => {
    if (!group) return null;
    return (
      <Space size={8} wrap>
        <PermissionButton
          icon={<EditOutlined />}
          size="small"
          onClick={() => {
            if (permLoading || !canEdit) {
              message.warning('수정 권한이 없습니다.');
              return;
            }
            setEditModalOpen(true);
          }}
          require={updateGroup}
        >
          정보수정
        </PermissionButton>
        {group.groupType === 'USER_GROUP' && (
          <PermissionButton
            icon={<SettingOutlined />}
            size="small"
            onClick={() => {
              if (permLoading || !canEditUserMembers) {
                message.warning('멤버 편집 권한이 없습니다.');
                return;
              }
              setUserModalOpen(true);
            }}
            require={updateUserGroupMembers}
          >
            멤버편집
          </PermissionButton>
        )}
        {group.groupType === 'MENU_GROUP' && (
          <PermissionButton
            icon={<SettingOutlined />}
            size="small"
            onClick={() => {
              if (permLoading || !canEditMenuMembers) {
                message.warning('메뉴 편집 권한이 없습니다.');
                return;
              }
              setMenuModalOpen(true);
            }}
            require={updateMenuGroupMembers}
          >
            메뉴편집
          </PermissionButton>
        )}
        {group.groupType === 'API_GROUP' && (
          <PermissionButton
            icon={<SettingOutlined />}
            size="small"
            onClick={() => {
              if (permLoading || !canEditApiMembers) {
                message.warning('API 편집 권한이 없습니다.');
                return;
              }
              setApiModalOpen(true);
            }}
            require={updateApiGroupMembers}
          >
            API편집
          </PermissionButton>
        )}
        <Popconfirm
          title="그룹 삭제"
          description="이 그룹을 삭제하시겠습니까?"
          okText="삭제"
          okType="danger"
          cancelText="취소"
          onConfirm={handleDelete}
          disabled={permLoading || !canDelete}
        >
          <PermissionButton
            danger
            icon={<DeleteOutlined />}
            size="small"
            require={deleteGroup}
            loading={deleting}
          >
            삭제
          </PermissionButton>
        </Popconfirm>
      </Space>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      destroyOnClose
      width={640}
      title={group ? `${TYPE_LABEL[group.groupType] || '그룹'} 상세` : '그룹 상세'}
      extra={renderHeaderActions()}
    >
      {!group ? (
        <Text type="secondary">그룹을 선택하세요.</Text>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="그룹 ID">{group.groupId}</Descriptions.Item>
            <Descriptions.Item label="그룹명">{group.groupNm || '-'}</Descriptions.Item>
            <Descriptions.Item label="타입">
              <Tag color={TYPE_COLOR[group.groupType] || 'default'}>
                {TYPE_LABEL[group.groupType] || group.groupType}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="설명">{group.groupDsctn || '-'}</Descriptions.Item>
            <Descriptions.Item label="대상 수">{Array.isArray(group.trgtIdList) ? group.trgtIdList.length : 0}</Descriptions.Item>
            <Descriptions.Item label="등록자">{group.rgtr || '-'} / {group.regDt || '-'}</Descriptions.Item>
            <Descriptions.Item label="수정자">{group.mdfr || '-'} / {group.mdfcnDt || '-'}</Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '16px 0 12px' }}>
            멤버 목록 ({members.length})
          </Divider>

          {renderMembers()}
        </Space>
      )}

      <GroupUserMembersModal
        open={userModalOpen}
        group={group?.groupType === 'USER_GROUP' ? group : null}
        onCancel={() => setUserModalOpen(false)}
        onSubmit={handleUserMembersSubmit}
      />
      <GroupEditModal
        open={editModalOpen}
        group={group}
        onCancel={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
      />
      <GroupMenuMembersModal
        open={menuModalOpen}
        group={group?.groupType === 'MENU_GROUP' ? group : null}
        onCancel={() => setMenuModalOpen(false)}
        onSuccess={handleMenuMembersSuccess}
      />
      <GroupApiMembersModal
        open={apiModalOpen}
        group={group?.groupType === 'API_GROUP' ? group : null}
        onCancel={() => setApiModalOpen(false)}
        onSuccess={handleApiMembersSuccess}
      />
    </Drawer>
  );
};

export default GroupDetailDrawer;
